'use client';

import {
  Fragment,
  createElement,
  type ElementType,
  type ReactNode,
} from 'react';
import { useEditMode } from './context';

type Props<T> = {
  path: string;
  items: T[];
  as?: ElementType;
  className?: string;
  emptyTemplate?: () => T;
  children: (item: T, itemPath: string, index: number) => ReactNode;
};

export function EditableList<T>({
  path,
  items,
  as = 'div',
  className,
  emptyTemplate,
  children,
}: Props<T>) {
  const { editing, setByPath } = useEditMode();

  const rendered = items.map((it, i) => (
    <Fragment key={i}>{children(it, `${path}.${i}`, i)}</Fragment>
  ));
  const list = createElement(as, { className }, rendered);

  if (!editing) return <>{list}</>;

  const add = () => {
    const next = emptyTemplate ? emptyTemplate() : ({} as T);
    setByPath(path, [...items, next]);
  };
  const remove = (i: number) => {
    setByPath(
      path,
      items.filter((_, idx) => idx !== i),
    );
  };
  const moveUp = (i: number) => {
    if (i === 0) return;
    const copy = items.slice();
    const prev = copy[i - 1] as T;
    copy[i - 1] = copy[i] as T;
    copy[i] = prev;
    setByPath(path, copy);
  };

  return (
    <>
      {list}
      <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-dashed border-slate-300 bg-slate-50 p-2 text-xs">
        <span className="font-medium text-slate-600">Itens:</span>
        {items.length === 0 && (
          <span className="text-slate-400">-sem itens —</span>
        )}
        {items.map((_, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-0.5 rounded bg-white px-1.5 py-0.5 ring-1 ring-slate-200"
          >
            <span className="px-1 text-slate-500">#{i + 1}</span>
            <button
              type="button"
              className="rounded px-1 hover:bg-slate-100 disabled:opacity-30"
              onClick={() => moveUp(i)}
              disabled={i === 0}
              title="Mover para cima"
            >
              ↑
            </button>
            <button
              type="button"
              className="rounded px-1 text-red-600 hover:bg-red-50"
              onClick={() => remove(i)}
              title="Remover"
            >
              ×
            </button>
          </span>
        ))}
        <button
          type="button"
          className="rounded bg-slate-900 px-2 py-1 text-white hover:bg-slate-700"
          onClick={add}
        >
          + Adicionar
        </button>
      </div>
    </>
  );
}
